import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header1 from "../components/default-header";
import DefaultFooter from "../components/default-footer";
import { VisaPack, VisaSearchParams } from "../types/visa";
import { API_URL } from "../config";

const VisaSearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchState = location.state as VisaSearchParams | null;

  const [packs, setPacks] = useState<VisaPack[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // État pour le modal d'authentification
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [selectedPack, setSelectedPack] = useState<VisaPack | null>(null);

  const isAuthenticated = !!localStorage.getItem("customer_silverjarz");

  useEffect(() => {
    if (!searchState) {
      navigate("/search-visa");
      return;
    }

    const fetchPacks = async () => {
      try {
        setLoading(true);

        const res = await axios.get(`${API_URL}/visa-packs/search`, {
          params: {
            destination_country_id: searchState.destination_country_id,
            nationality_id: searchState.nationality_id,
          },
        });
        setPacks(res.data.data || []);
      } catch (err) {
        console.error("Error fetching visa packs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, [searchState, navigate]);

  // Action du bouton Apply Now
  const handleApplyClick = (pack: VisaPack) => {
    // ✅ Récupérer la nationalité depuis searchState (maintenant disponible)
    const nationalityName = searchState?.nationality_name || "";

    if (isAuthenticated) {
      navigate("/visa-application", {
        state: {
          visaPack: pack,
          isGuest: false,
          nationality: nationalityName, // ✅ Passer le nom de la nationalité
        },
      });
    } else {
      setSelectedPack(pack);
      setShowAuthModal(true);
    }
  };

  // Option : Continuer en tant qu'invité
  const handleContinueAsGuest = () => {
    setShowAuthModal(false);
    if (selectedPack) {
      const nationalityName = searchState?.nationality_name || "";
      navigate("/visa-application", {
        state: {
          visaPack: selectedPack,
          isGuest: true,
          nationality: nationalityName, // ✅ Ajouter pour le mode invité
        },
      });
    }
  };

  // Option : Connexion / Inscription
  const handleLogin = () => {
    setShowAuthModal(false);
    const nationalityName = searchState?.nationality_name || "";
    navigate("/login", { 
      state: { 
        redirectTo: "/visa-application", 
        pack: selectedPack,
        nationality: nationalityName, // ✅ Passer la nationalité au login
      } 
    });
  };


  return (
    <div className="va-root vsr-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');

        .va-root {
          --va-primary: #294b5a;
          --va-primary-dark: #1b343f;
          --va-gold: #d3be76;
          --va-gold-dark: #b8974c;
          --va-paper: #f7f4ee;
          --va-card: #ffffff;
          --va-border: #e4ded0;
          --va-ink: #22343b;
          --va-muted: #748187;
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--va-paper);
          color: var(--va-ink);
          min-height: 100vh;
        }
        .va-root * { box-sizing: border-box; }
        .va-serif { font-family: 'Fraunces', 'Georgia', serif; }
        .va-mono { font-family: 'IBM Plex Mono', monospace; }

        /* ---------- Header band ---------- */
        .vsr-hero {
          background:
            radial-gradient(circle at 85% 15%, rgba(211,190,118,0.10), transparent 45%),
            radial-gradient(circle at 6% 90%, rgba(211,190,118,0.06), transparent 40%),
            var(--va-primary);
          color: #fff;
          padding: 100px 32px 60px;
          position: relative;
          overflow: hidden;
        }
        .vsr-hero::before {
          content: "";
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px);
          background-size: 14px 14px;
          opacity: .35;
          pointer-events: none;
        }
        .vsr-hero-inner {
          max-width: 1180px; margin: 0 auto; position: relative;
          display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; flex-wrap: wrap;
        }
        .vsr-eyebrow {
          letter-spacing: .18em; text-transform: uppercase; font-size: 11.5px;
          color: var(--va-gold); font-weight: 600; display: flex; align-items: center; gap: 8px;
        }
        .vsr-eyebrow .line { width: 22px; height: 1px; background: var(--va-gold); display: inline-block; }
        .vsr-hero h1 { font-size: 32px; line-height: 1.15; font-weight: 600; margin: 12px 0 6px; color: #fff; }
        .vsr-hero p { font-size: 14px; color: rgba(255,255,255,0.72); margin: 0; }
        .vsr-hero p strong { color: var(--va-gold); font-weight: 700; }

        .vsr-modify-btn {
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.28) !important;
          color: #fff; padding: 11px 20px; border-radius: 999px; cursor: pointer;
          font-weight: 600; font-size: 13px; display: inline-flex; align-items: center; gap: 8px;
          transition: all .15s ease; white-space: nowrap;
        }
        .vsr-modify-btn:hover { background: rgba(255,255,255,0.16); border-color: var(--va-gold) !important; }

        /* ---------- Shell ---------- */
        .vsr-shell { max-width: 1180px; margin: -32px auto 0; padding: 0 32px 80px; position: relative; z-index: 2; }

        /* ---------- Loading / empty ---------- */
        .vsr-state {
          text-align: center; padding: 80px 20px; background: var(--va-card);
          border: 1px solid var(--va-border); border-radius: 14px;
        }
        .vsr-spinner {
          width: 40px; height: 40px; border-radius: 50%;
          border: 3px solid var(--va-border); border-top-color: var(--va-gold);
          margin: 0 auto 18px; animation: vsr-spin 0.8s linear infinite;
        }
        @keyframes vsr-spin { to { transform: rotate(360deg); } }
        .vsr-state p { color: var(--va-muted); font-size: 14px; margin: 0; }
        .vsr-state-icon {
          width: 64px; height: 64px; border-radius: 50%; background: var(--va-paper);
          display: flex; align-items: center; justify-content: center; margin: 0 auto 18px;
          color: var(--va-primary); border: 1px dashed var(--va-border);
        }
        .vsr-state h3 { font-size: 18px; color: var(--va-ink); margin: 0 0 6px; font-family: 'Fraunces', serif; }

        /* ---------- Grid ---------- */
        .vsr-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 24px; }

        /* ---------- Card ---------- */
        .vsr-card {
          background: var(--va-card); border: 1px solid var(--va-border) !important; border-radius: 14px;
          padding: 26px; display: flex; flex-direction: column; justify-content: space-between;
          position: relative; transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
        }
        .vsr-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 36px -22px rgba(41,75,90,0.35);
          border-color: var(--va-gold) !important;
        }
        .vsr-card::before {
          content: ""; position: absolute; top: 12px; left: 12px; width: 16px; height: 16px;
          border-top: 2px solid var(--va-gold); border-left: 2px solid var(--va-gold); border-radius: 2px 0 0 0;
          opacity: 0; transition: opacity .18s ease;
        }
        .vsr-card:hover::before { opacity: 1; }

        .vsr-card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; gap: 10px; }
        .vsr-badge {
          font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
          background: #f0f5ee; color: #4a7a4f; padding: 5px 11px; border-radius: 999px; white-space: nowrap;
        }
        .vsr-price { font-size: 19px; font-weight: 700; color: var(--va-primary); text-align: right; white-space: nowrap; }
        .vsr-price span { display: block; font-size: 10.5px; font-weight: 600; color: var(--va-muted); text-transform: uppercase; letter-spacing: .06em; }

        .vsr-card h3 { font-size: 19px; font-weight: 600; color: var(--va-ink); margin: 0 0 8px; }
        .vsr-card-desc { font-size: 13px; color: var(--va-muted); line-height: 1.55; margin-bottom: 18px; }

        .vsr-details {
          border-top: 1px dashed var(--va-border); padding-top: 13px; margin-bottom: 20px; font-size: 12.5px;
        }
        .vsr-detail-row { display: flex; justify-content: space-between; margin-bottom: 8px; color: var(--va-muted); }
        .vsr-detail-row:last-child { margin-bottom: 0; }
        .vsr-detail-row strong { color: var(--va-ink); font-weight: 600; }

        .vsr-apply-btn {
          width: 100%; background: var(--va-primary); color: #fff; border: none;
          padding: 13px; border-radius: 9px; font-weight: 600; font-size: 14px; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: background .15s ease;
        }
        .vsr-apply-btn:hover { background: var(--va-primary-dark); }

        /* ---------- Modal ---------- */
        .vsr-modal-overlay {
          position: fixed; inset: 0; background: rgba(27, 52, 63, 0.55); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px;
        }
        .vsr-modal {
          background: var(--va-card); border-radius: 16px; max-width: 440px; width: 100%;
          padding: 36px 32px 32px; box-shadow: 0 30px 60px -20px rgba(27,52,63,0.45);
          text-align: center; position: relative; border: 1px solid var(--va-border);
        }
        .vsr-modal-close {
          position: absolute; top: 16px; right: 16px; border: none; background: transparent;
          font-size: 16px; cursor: pointer; color: var(--va-muted); width: 30px; height: 30px;
          border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: background .15s ease;
        }
        .vsr-modal-close:hover { background: var(--va-paper); color: var(--va-ink); }
        .vsr-modal-icon {
          width: 60px; height: 60px; border-radius: 50%; background: var(--va-paper);
          color: var(--va-primary); display: flex; align-items: center; justify-content: center;
          margin: 0 auto 18px; border: 1.5px dashed var(--va-gold); font-size: 22px;
        }
        .vsr-modal h2 { font-size: 21px; font-weight: 600; color: var(--va-ink); margin: 0 0 8px; font-family: 'Fraunces', serif; }
        .vsr-modal p { font-size: 13.5px; color: var(--va-muted); margin: 0 0 26px; line-height: 1.55; }
        .vsr-modal-actions { display: flex; flex-direction: column; gap: 12px; }
        .vsr-btn-primary {
          width: 100%; background: var(--va-gold); color: var(--va-primary-dark); border: none;
          padding: 13px; border-radius: 9px; font-weight: 700; font-size: 14.5px; cursor: pointer; transition: background .15s ease;
        }
        .vsr-btn-primary:hover { background: var(--va-gold-dark); }
        .vsr-btn-ghost {
          width: 100%; background: transparent; color: var(--va-ink); border: 1px solid var(--va-border) !important;
          padding: 13px; border-radius: 9px; font-weight: 600; font-size: 14.5px; cursor: pointer; transition: all .15s ease;
        }
        .vsr-btn-ghost:hover { border-color: var(--va-primary) !important; }
      `}</style>

      <Header1 />

      {/* HERO */}
      <div className="vsr-hero">
        <div className="vsr-hero-inner">
          <div>
            <div className="vsr-eyebrow"><span className="line" />Search Results</div>
            <h1 className="va-serif">Available Visa Packages</h1>
            <p>
              <strong>{packs.length}</strong> package{packs.length !== 1 ? "s" : ""} matching your search criteria
            </p>
          </div>
          <button className="vsr-modify-btn" onClick={() => navigate(-1)}>
            ← Modify Search
          </button>
        </div>
      </div>

      <div className="vsr-shell">
        {loading ? (
          <div className="vsr-state">
            <div className="vsr-spinner" />
            <p>Searching the best visa offers for you...</p>
          </div>
        ) : packs.length === 0 ? (
          <div className="vsr-state">
            <div className="vsr-state-icon">
              <i className="fa-solid fa-passport" style={{ fontSize: 24 }}></i>
            </div>
            <h3>No visa packages found</h3>
            <p>We couldn't find any visa package matching this specific selection.</p>
          </div>
        ) : (
          <div className="vsr-grid">
            {packs.map((pack) => {
              return (
                <div key={pack.id} className="vsr-card">
                  <div>
                    <div className="vsr-card-top">
                      <span className="vsr-badge">{pack.visa_type?.name || "Standard Visa"}</span>
                      <span className="vsr-price va-mono">
                        <span>Total</span>
                        {pack.price} {pack.currency || "OMR"}
                      </span>
                    </div>

                    <h3 className="va-serif">{pack.name_en}</h3>

                    <p className="vsr-card-desc">
                      {pack.description_en || "Complete visa processing service with expert application review."}
                    </p>

                    <div className="vsr-details">
                      <div className="vsr-detail-row">
                        <span>Processing time</span>
                        <strong>{pack.processing_time_en}</strong>
                      </div>
                      <div className="vsr-detail-row">
                        <span>Validity</span>
                        <strong>{pack.visa_validity_en}</strong>
                      </div>
                      <div className="vsr-detail-row">
                        <span>Entry type</span>
                        <strong>{pack.entry_type}</strong>
                      </div>
                    </div>
                  </div>

                  <button className="vsr-apply-btn" onClick={() => handleApplyClick(pack)}>
                    Apply Now →
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL */}
      {showAuthModal && (
        <div className="vsr-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="vsr-modal" onClick={(e) => e.stopPropagation()}>
            <button className="vsr-modal-close" onClick={() => setShowAuthModal(false)}>✕</button>

            <div className="vsr-modal-icon">
              <i className="fa-solid fa-user-lock"></i>
            </div>

            <h2>How would you like to proceed?</h2>
            <p>
              Sign in to track your application easily, or continue as a guest to fill in your details right away.
            </p>

            <div className="vsr-modal-actions">
              <button className="vsr-btn-primary" onClick={handleLogin}>
                Sign In / Register
              </button>
            </div>
          </div>
        </div>
      )}

      <DefaultFooter />
    </div>
  );
};

export default VisaSearchResults;